import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, MessageCircle, Share2, User, X, Maximize2, ChevronDown } from 'lucide-react';
import { Post } from '../types/Post';

interface FeedCardProps {
  post: Post;
}

export function FeedCard({ post }: FeedCardProps) {
  const [isLiked, setIsLiked] = useState(false);
  const [error, setError] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFullContent, setShowFullContent] = useState(false);
  
  // Fix image URL construction to handle all cases
  const imageUrl = post.image_url
    ? (post.image_url.startsWith('http') 
        ? post.image_url 
        : `https://lyfy.ltd${post.image_url.startsWith('/') ? post.image_url : `/${post.image_url}`}`)
    : null;

  const handleImageError = () => {
    console.error('Failed to load image:', imageUrl);
    setError(true);
  };

  const formatContent = (content: string) => {
    // Split content into parts that should be processed differently
    const parts = content.split(/(\s+|[,.!?])/);
    
    return parts.map((part, index) => {
      // Handle hashtags
      if (part.startsWith('#')) {
        return (
          <span
            key={index}
            className="text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 cursor-pointer font-medium transition-colors"
            onClick={() => console.log('Hashtag clicked:', part)}
          >
            {part}
          </span>
        );
      }
      
      // Handle URLs
      if (part.match(/^(https?:\/\/[^\s]+)/)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 hover:underline transition-colors"
          >
            {part}
          </a>
        );
      }
      
      // Handle @mentions
      if (part.startsWith('@')) {
        return (
          <span
            key={index}
            className="text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 cursor-pointer font-medium transition-colors"
            onClick={() => console.log('Mention clicked:', part)}
          >
            {part}
          </span>
        );
      }
      
      // Return regular text
      return part;
    });
  };

  const shouldTruncate = post.content.length > 30;
  const truncatedContent = shouldTruncate && !showFullContent 
    ? post.content.slice(0, 30) + '...' 
    : post.content;

  return (
    <>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-4 sm:p-6 mb-6 hover:shadow-xl transition-all duration-300 w-full max-w-2xl mx-auto cursor-pointer"
        onClick={() => setIsExpanded(true)}
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center shadow-md">
              <User className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-300" />
            </div>
          </div>
          
          <div className="flex-grow space-y-4 w-full">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 pb-3">
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white text-base sm:text-lg group-hover:text-blue-500 transition-colors">
                  {post.title}
                  <Maximize2 className="inline-block w-4 h-4 ml-2 text-gray-400 group-hover:text-blue-500" />
                </h3>
                {post.createdAt && (
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {new Date(post.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-gray-800 dark:text-gray-200 text-sm sm:text-base leading-relaxed break-words">
                {formatContent(truncatedContent)}
                {shouldTruncate && !showFullContent && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFullContent(true);
                    }}
                    className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 font-medium ml-2"
                  >
                    Read more
                  </button>
                )}
              </p>

              {imageUrl && !error && (
                <div className="relative rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700 shadow-inner max-h-[300px] sm:max-h-[400px]">
                  <img
                    src={imageUrl}
                    alt={post.title || 'Post image'}
                    onError={handleImageError}
                    className="w-full h-full object-cover rounded-xl hover:opacity-95 transition-opacity"
                    loading="lazy"
                  />
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-6 pt-4 mt-2 border-t border-gray-100 dark:border-gray-700">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsLiked(!isLiked);
                }}
                className={`flex items-center gap-2 text-xs sm:text-sm font-medium px-3 sm:px-4 py-1.5 sm:py-2 rounded-full transition-all duration-200 ${
                  isLiked 
                    ? 'text-pink-500 dark:text-pink-400 bg-pink-50 dark:bg-pink-900/20' 
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${isLiked ? 'fill-current' : ''}`} />
                <span className="whitespace-nowrap">Like</span>
              </button>
              <button 
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
              >
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="whitespace-nowrap">Comment</span>
              </button>
              <button 
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
              >
                <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="whitespace-nowrap">Share</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {isExpanded && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white dark:bg-gray-800 p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{post.title}</h2>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              
              <div className="p-4 sm:p-6 space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center shadow-md">
                    <User className="w-6 h-6 text-blue-600 dark:text-blue-300" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white text-lg">Administrator</h3>
                    {post.createdAt && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(post.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  <p className="text-gray-800 dark:text-gray-200 text-base sm:text-lg leading-relaxed">
                    {formatContent(post.content)}
                  </p>

                  {imageUrl && !error && (
                    <div className="relative rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700 shadow-inner">
                      <img
                        src={imageUrl}
                        alt={post.title || 'Post image'}
                        onError={handleImageError}
                        className="w-full object-contain max-h-[600px] rounded-xl"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-6 pt-6 border-t border-gray-100 dark:border-gray-700">
                  <button 
                    onClick={() => setIsLiked(!isLiked)}
                    className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full transition-all duration-200 ${
                      isLiked 
                        ? 'text-pink-500 dark:text-pink-400 bg-pink-50 dark:bg-pink-900/20' 
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
                    <span>Like</span>
                  </button>
                  <button 
                    className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300 px-4 py-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span>Comment</span>
                  </button>
                  <button 
                    className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300 px-4 py-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                  >
                    <Share2 className="w-5 h-5" />
                    <span>Share</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
